import { Injectable } from "@angular/core";

export interface BrowserInfo {
  name: string;
  version: number;
  isSupported: boolean;
}

@Injectable({
  providedIn: "root",
})
export class BrowserDetectionService {
  /**
   * Minimum supported browser versions based on Angular 20 requirements
   * https://angular.dev/reference/versions#browser-support
   * which refers to the core browser set of 30-04-2025:
   * https://web-platform-dx.github.io/web-features/supported-browsers/?widelyAvailableOnDate=2025-04-30&includeDownstream=false)
   * The versions listed here should be the same as in browserslist in package.json.
   */
  private readonly minSupportedVersions = {
    chrome: 107,
    edge: 107,
    firefox: 104,
    safari: 16,
    appleWebKit: 605, // Baseline AppleWebKit build. Webview behaves like Safari. Update when Safari baseline changes.
  };

  /**
   * Detect current browser name and major version.
   */
  detectBrowser(): BrowserInfo {
    if (typeof navigator === "undefined") {
      return { name: "Unknown", version: 0, isSupported: false };
    }
    const userAgent = navigator.userAgent;

    const chromeInfo = this.detectChrome(userAgent);
    if (chromeInfo) return chromeInfo;

    const edgeInfo = this.detectEdge(userAgent);
    if (edgeInfo) return edgeInfo;

    const firefoxInfo = this.detectFirefox(userAgent);
    if (firefoxInfo) return firefoxInfo;

    const safariInfo = this.detectSafari(userAgent);
    if (safariInfo) return safariInfo;

    // Unknown or unsupported browser
    return {
      name: "Unknown",
      version: 0,
      isSupported: false,
    };
  }

  private detectChrome(userAgent: string): BrowserInfo | null {
    if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) {
      const match = /Chrome\/(\d+)/.exec(userAgent);
      const version = match ? Number.parseInt(match[1], 10) : 0;
      return {
        name: "Chrome",
        version,
        isSupported: version >= this.minSupportedVersions.chrome,
      };
    }
    return null;
  }

  private detectEdge(userAgent: string): BrowserInfo | null {
    if (userAgent.includes("Edg")) {
      const match = /Edg\/(\d+)/.exec(userAgent);
      const version = match ? Number.parseInt(match[1], 10) : 0;
      return {
        name: "Edge",
        version,
        isSupported: version >= this.minSupportedVersions.edge,
      };
    }
    return null;
  }

  private detectFirefox(userAgent: string): BrowserInfo | null {
    if (userAgent.includes("Firefox")) {
      const match = /Firefox\/(\d+)/.exec(userAgent);
      const version = match ? Number.parseInt(match[1], 10) : 0;
      return {
        name: "Firefox",
        version,
        isSupported: version >= this.minSupportedVersions.firefox,
      };
    }
    return null;
  }

  private detectSafari(userAgent: string): BrowserInfo | null {
    const matchSafari = /Version\/(\d+)\.(\d+)/.exec(userAgent);
    if (matchSafari) {
      const major = Number.parseInt(matchSafari[1], 10);
      return {
        name: "Safari",
        version: major,
        isSupported: major >= this.minSupportedVersions.safari,
      };
    } else {
      // AppleWebKit on Apple devices. WebView behaves like Safari.
      const matchAppleWebKit = /AppleWebKit\/(\d+)/.exec(userAgent);
      if (matchAppleWebKit && /iPhone|iPad|iPod|iOS|Mac OS X/i.test(userAgent)) {
        return {
          name: "Safari", // Treat AppleWebKit on iOS as Safari as student may not know the difference
          version: this.minSupportedVersions.safari, // Map AppleWebKit version to minimum Safari version
          isSupported: Number.parseInt(matchAppleWebKit[1], 10) >= this.minSupportedVersions.appleWebKit,
        };
      }
    }
    return null;
  }

  /**
   * Check if essential modern JavaScript features exist.
   */
  checkEssentialFeatures(): boolean {
    try {
      // Check for essential modern JavaScript features
      return !!(
        typeof globalThis.fetch === "function" &&
        typeof globalThis.Promise === "function" &&
        typeof globalThis.Map === "function" &&
        typeof globalThis.Set === "function" &&
        typeof Array.prototype.includes === "function" &&
        typeof Object.assign === "function" &&
        // Check for ES6+ features
        this.checkES6Features()
      );
    } catch {
      return false;
    }
  }

  private checkES6Features(): boolean {
    // Quick test for arrow functions, template literals and const
    try {
      const arrow = () => true;
      const template = `test`;
      const testConst = true;
      return arrow() && template === "test" && testConst;
    } catch {
      return false;
    }
  }

  /**
   * Shows a non-blocking warning overlay for unsupported browsers
   */
  showBrowserWarningOverlay(browserInfo: BrowserInfo): void {
    if (browserInfo.isSupported) return;
    if (typeof document === "undefined") return;
    // Do nothing if one already exists
    if (document.getElementById("osiris-browser-warning-toast")) {
      return;
    }

    const warningHTML = this.createWarningOverlayHTML(browserInfo);

    const overlay = document.createElement("div");
    overlay.id = "osiris-browser-warning-toast";
    overlay.innerHTML = warningHTML;
    overlay.style.position = "fixed";
    overlay.style.left = "50%";
    overlay.style.bottom = "16px";
    overlay.style.transform = "translateX(-50%)";
    overlay.style.zIndex = "10000";
    overlay.style.maxWidth = "460px";
    overlay.style.width = "calc(100% - 32px)";
    overlay.style.pointerEvents = "auto";

    // Close button
    const closeButton = overlay.querySelector(".close-button");
    const remove = () => overlay.remove();
    if (closeButton) {
      closeButton.addEventListener("click", remove);
    }

    document.body.appendChild(overlay);
  }

  /**
   * Build HTML for the warning toast.
   */
  private createWarningOverlayHTML(browserInfo: BrowserInfo): string {
    const supportMessageNl = `Beperkte ondersteuning voor ${browserInfo.name} ${browserInfo.version}. Sommige onderdelen werken mogelijk niet.
          Gebruik Edge ${this.minSupportedVersions.edge}, Chrome ${this.minSupportedVersions.chrome},
          Firefox ${this.minSupportedVersions.firefox}, Safari ${this.minSupportedVersions.safari} of nieuwer.`;
    const supportMessageEn = `Limited support for ${browserInfo.name} ${browserInfo.version}. Some features may not work.
          Please use Edge ${this.minSupportedVersions.edge}, Chrome ${this.minSupportedVersions.chrome},
          Firefox ${this.minSupportedVersions.firefox}, Safari ${this.minSupportedVersions.safari} or newer.`;

    return `
      <div role="alert" aria-live="polite" style="
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        background:#ffffff;
        color:#000;
        border:1px solid #ddd;
        border-radius:20px;
        overflow:hidden;
        padding:16px 18px 14px 18px;
        box-shadow:0 8px 28px rgba(0,0,0,0.28), 0 2px 6px rgba(0,0,0,0.20);
        animation:osiris-toast-in .25s ease;
        position:relative;
      ">
        <style>
          @keyframes osiris-toast-in { from { opacity:0; transform:translate(-50%,10px);} to { opacity:1; transform:translate(-50%,0);} }
          .osiris-toast-title { font-size:13px; font-weight:600; line-height:1.35; display:flex; align-items:center; gap:8px; margin-bottom:8px; }
          .osiris-toast-text { font-size:11px; line-height:1.35; margin:0 0 8px 0; color:#222; }
          .osiris-toast-text em { font-style:italic; font-size:inherit; font-weight:inherit; }
          .osiris-toast-text a { font-size:inherit; }
        </style>
        <button class="close-button" aria-label="Close" style="
          position:absolute;
          top:6px;
          right:10px;
          background:transparent;
          border:none;
          font-size:20px;
          line-height:1;
          cursor:pointer;
          color:#444;
        ">×</button>
        <div class="osiris-toast-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false" style="flex-shrink:0;">
            <path d="M12 9v4"></path>
            <path d="M12 17h.01"></path>
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          </svg>
          <strong>Support warning</strong>
        </div>
        <p class="osiris-toast-text">
          ${supportMessageNl}
        </p>
        <p class="osiris-toast-text">
          <em>${supportMessageEn}</em>
        </p>
      </div>
    `;
  }
}
